/**
 * ─── VENDORED FROM arrow-fe ────────────────────────────────────────────────
 *
 * Source of truth: `arrow-fe/src/lib/lib/api-client.ts`.
 *
 * This is a copy, not a fork. The panel speaks the same sealed-envelope
 * protocol as the storefront (docs/backend/ENCRYPTION.md), and two
 * implementations of a security protocol that drift is a real liability — so
 * changes belong upstream first, then get copied here.
 *
 * If this ever needs to diverge from the storefront's copy, that is the signal
 * to stop copying and extract a shared workspace package instead
 * (ADMIN.md §4). Do not "fix" it here in isolation.
 * ───────────────────────────────────────────────────────────────────────────
 */

import { clearAccessToken, getAccessToken, setAccessToken } from "@/lib/api/tokens";
import type { ApiResponse, AuthResult } from "@/lib/api/types";
import {
  isResponseEnvelope,
  openResponse,
  sealBinary,
  sealRequest,
  toQueryParam,
  type Handshake,
  type Session,
} from "@/lib/crypto/envelope";
import { encodeFrame, type UploadMetadata } from "@/lib/crypto/upload-frame";
import { encryptPayloads, env } from "@/lib/env";

/**
 * Typed wrapper over `fetch`, pointed at the backend API.
 *
 * In production every payload it sends and every response it reads is a sealed
 * envelope (`src/lib/crypto/envelope.ts`). In development it sends plain JSON,
 * because a developer needs to read the Network tab. Callers see no difference:
 * pass a path, a body, and a query, and get typed data back.
 *
 * ── Why `query` is an option and not part of the path ──────────────────────
 *
 * A filter or a page number in a URL is as readable as a body, so it is
 * encrypted too and travels inside the envelope. A query string written into
 * `path` is lifted out and sealed with everything else, so both call styles
 * work — but passing `query` is the one that will not surprise anyone.
 */

const DEFAULT_TIMEOUT_MS = 10_000;

/** A handshake outlives many requests; refetching one per call is a wasted RTT. */
const HANDSHAKE_TTL_MS = 30 * 60_000;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    message: string,
    /** The API's stable `code`, when the response carried one. */
    readonly code?: string,
    /**
     * The error's structured payload, when it has one.
     *
     * Typed `unknown` on purpose: its shape depends entirely on `code`, so a
     * caller has to check the code before reading it. `VALIDATION_FAILED`
     * carries field-level issues; `CART_STALE` carries the lines that changed,
     * which is the difference between "your order failed" and "the T5 is now
     * ₹1,999".
     */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  query?: Record<string, unknown>;
  timeoutMs?: number;
  /**
   * Skips both the `Authorization` header and the refresh-and-retry dance.
   *
   * Set on the auth endpoints themselves — signing in while holding an expired
   * token should not trigger a refresh, and `POST /v1/auth/refresh` retrying
   * itself on failure is an infinite loop with extra steps.
   */
  skipAuth?: boolean;
};

type ErrorBody = { error?: { code?: string; message?: string; details?: unknown } };

let cached: { at: number; handshake: Promise<Handshake> } | null = null;

/**
 * Fetches and caches the server's public key.
 *
 * The promise is cached, not the resolved value, so ten calls made at once
 * during a first page render share one round trip rather than racing.
 */
async function getHandshake(): Promise<Handshake> {
  if (cached && Date.now() - cached.at < HANDSHAKE_TTL_MS) {
    return cached.handshake;
  }

  const request = (async (): Promise<Handshake> => {
    const url = new URL("/v1/crypto/handshake", env.NEXT_PUBLIC_API_BASE_URL).toString();
    const response = await fetch(url, { signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS) });

    if (!response.ok) {
      throw new ApiError(response.status, url, "Could not start a secure session.");
    }

    const { data } = (await response.json()) as { data: Handshake };
    return data;
  })();

  cached = { at: Date.now(), handshake: request };

  // A failed handshake must not be cached, or one blip poisons the tab.
  request.catch(() => {
    cached = null;
  });

  return request;
}

/** Drops the cached key. Called when the server says it cannot open an envelope. */
function forgetHandshake(): void {
  cached = null;
}

function splitQuery(
  path: string,
  extra: Record<string, unknown> | undefined,
): { pathname: string; query: Record<string, unknown> } {
  const url = new URL(path, env.NEXT_PUBLIC_API_BASE_URL);
  const query: Record<string, unknown> = {};

  for (const [key, value] of url.searchParams) query[key] = value;

  return { pathname: url.pathname, query: { ...query, ...extra } };
}

function absolute(pathname: string, search?: Record<string, string>): string {
  const url = new URL(pathname, env.NEXT_PUBLIC_API_BASE_URL);

  for (const [key, value] of Object.entries(search ?? {})) {
    url.searchParams.set(key, value);
  }

  return url.toString();
}

/**
 * Reads a response, opening it first if it was sealed.
 *
 * The `X-Arrow-Encrypted` header is not what decides: a failure raised before
 * the server could derive a key — a rejected envelope, a rate limit — comes
 * back as readable JSON even on an otherwise-encrypted API, so the body's shape
 * is what is checked.
 */
async function readBody(response: Response, session: Session | null): Promise<unknown> {
  if (response.status === 204) return undefined;

  const text = await response.text();
  if (text.length === 0) return undefined;

  const parsed: unknown = JSON.parse(text);

  if (session && isResponseEnvelope(parsed)) {
    return openResponse(session, parsed, response.status);
  }

  return parsed;
}

function toApiError(url: string, response: Response, body: unknown): ApiError {
  const { error } = (body ?? {}) as ErrorBody;

  return new ApiError(
    response.status,
    url,
    error?.message ?? `Request failed: ${response.status} ${response.statusText}`,
    error?.code,
    error?.details,
  );
}

/**
 * `ENCRYPTION_FAILED` means the server could not open the envelope — which it
 * decides *before* routing, so the request had no effect and replaying it is
 * safe even for a POST. In practice it means this tab is holding a key that has
 * since rotated, and the fix is to handshake again.
 */
function shouldRehandshake(error: unknown): boolean {
  return error instanceof ApiError && error.code === "ENCRYPTION_FAILED";
}

/**
 * `TOKEN_EXPIRED` is deliberately its own code, distinct from
 * `UNAUTHENTICATED`: it means the session is still good and the 15-minute
 * access token simply aged out, so the client should refresh rather than send
 * the shopper back to a sign-in form mid-checkout.
 */
function shouldRefresh(error: unknown): boolean {
  return error instanceof ApiError && error.code === "TOKEN_EXPIRED";
}

/**
 * One refresh at a time, for the whole tab.
 *
 * A page that fires the cart, the address book and the order list together
 * would otherwise start three rotations for the same session — and since
 * refresh tokens rotate, two of them would present a token the first had
 * already spent. The API answers the loser with a 401, and whichever response
 * lands last is the one whose `Set-Cookie` the browser keeps: lose that race
 * and a live session is signed out at random.
 *
 * **Every caller must come through here** — including the cold-load restore in
 * `lib/api/auth.ts`, which used to call the endpoint directly and so shared
 * nothing with this promise. React StrictMode mounts effects twice in
 * development, which made that path fire two rotations on literally every page
 * load. Exported for exactly that reason.
 *
 * Returns the whole `AuthResult`, not just a flag, because the restore path
 * needs the user as well; `null` means this tab is signed out, which is the
 * normal case for a visitor and not an error.
 */
let refreshing: Promise<AuthResult | null> | null = null;

export async function renewSession(): Promise<AuthResult | null> {
  refreshing ??= (async () => {
    try {
      const { data } = await attempt<ApiResponse<AuthResult>>("/v1/auth/refresh", {
        method: "POST",
        body: {},
        skipAuth: true,
      });

      setAccessToken(data.accessToken);
      return data;
    } catch {
      // No cookie, an expired one, or a revoked family. All of them mean the
      // same thing to the UI: this tab is signed out.
      clearAccessToken();
      return null;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await attempt<T>(path, options);
  } catch (error) {
    if (shouldRehandshake(error)) {
      forgetHandshake();
      // Once. A second failure is a real problem, not a stale key.
      return attempt<T>(path, options);
    }

    if (!options.skipAuth && shouldRefresh(error)) {
      const renewed = await renewSession();
      if (!renewed) throw error;

      return attempt<T>(path, options);
    }

    throw error;
  }
}

async function attempt<T>(path: string, options: RequestOptions): Promise<T> {
  const { body, query, timeoutMs, headers, method, skipAuth, ...rest } = options;
  const verb = method ?? (body === undefined ? "GET" : "POST");
  const { pathname, query: merged } = splitQuery(path, query);

  const signal = rest.signal ?? AbortSignal.timeout(timeoutMs ?? DEFAULT_TIMEOUT_MS);

  /**
   * The bearer token, when there is one.
   *
   * Sent as a header rather than inside the envelope because the API reads it
   * in `requireAuth`, which runs before anything is decrypted — and because a
   * header is what every HTTP tool in the world already understands. It is not
   * a secret the envelope needs to hide: it is short-lived, scoped to one user,
   * and already travelling over TLS.
   */
  const token = skipAuth ? null : getAccessToken();

  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  /**
   * `include`, not the default `same-origin`.
   *
   * The storefront is `www.arrowindia.com` and the API is
   * `api.arrowindia.com` — a different origin, so the refresh cookie is only
   * sent if this says so. They share a registrable domain, which is what lets
   * that cookie be `SameSite=Lax` rather than `None`; `include` is what makes
   * the browser actually attach it. Without this, sign-in appears to work and
   * then the session silently ends fifteen minutes later.
   */
  const credentials: RequestCredentials = "include";

  /**
   * `Content-Type` only when there is something to type.
   *
   * `application/json` is not a CORS-safelisted value, so sending it on a
   * bodyless GET turns every catalogue read into a preflight plus the real
   * request — two round trips for the endpoint PLAN.md §4.2.1 calls the
   * highest-traffic surface in the system, and the specific thing its "keep the
   * snapshot a simple request" rule exists to prevent. A GET carries its
   * envelope in `?d=`, so it has no body and needs no content type.
   */
  const bodyHeaders: Record<string, string> =
    verb === "GET" || verb === "HEAD" ? {} : { "Content-Type": "application/json" };

  let url: string;
  let init: RequestInit;
  let session: Session | null = null;

  if (encryptPayloads) {
    const sealed = await sealRequest(await getHandshake(), verb, { body, query: merged });
    session = sealed.session;

    // A GET has no body to put an envelope in, so it rides in `?d=` — which is
    // then the only query parameter the request has.
    const inUrl = verb === "GET" || verb === "HEAD";

    url = absolute(pathname, inUrl ? { d: toQueryParam(sealed.envelope) } : undefined);
    init = {
      ...rest,
      method: verb,
      signal,
      credentials,
      headers: { ...bodyHeaders, ...authHeaders, ...headers },
      ...(inUrl ? {} : { body: JSON.stringify(sealed.envelope) }),
    };
  } else {
    url = absolute(
      pathname,
      Object.fromEntries(Object.entries(merged).map(([key, value]) => [key, String(value)])),
    );
    init = {
      ...rest,
      method: verb,
      signal,
      credentials,
      headers: { ...bodyHeaders, ...authHeaders, ...headers },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    };
  }

  const response = await fetch(url, init);
  const parsed = await readBody(response, session);

  if (!response.ok) throw toApiError(url, response, parsed);

  return parsed as T;
}

/**
 * Uploads an image with its bytes and its name sealed together.
 *
 * The obvious alternative — a presigned S3 URL and a direct PUT from the
 * browser — cannot be encrypted: S3 holds no key, so the image would cross the
 * network in the clear and sit in the Network tab as a plain image preview.
 * These bytes go through the API instead, which is the only party that can
 * decrypt them.
 *
 * Outside production this posts the same frame unencrypted, and the API's
 * development mode reads it with the same parser.
 */
export async function uploadImage<T>(
  path: string,
  file: File | { metadata: UploadMetadata; bytes: Uint8Array },
  options: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<T> {
  try {
    return await attemptUpload<T>(path, file, options);
  } catch (error) {
    if (shouldRehandshake(error)) {
      forgetHandshake();

      return attemptUpload<T>(path, file, options);
    }

    /**
     * The same refresh-and-replay `apiFetch` performs, and it is not optional
     * here: an access token lives 15 minutes, an operator can easily spend that
     * long writing a product before picking a photograph, and without this the
     * upload fails with "Sign in to continue" on a session that is perfectly
     * alive.
     */
    if (shouldRefresh(error)) {
      const renewed = await renewSession();
      if (!renewed) throw error;

      return attemptUpload<T>(path, file, options);
    }

    throw error;
  }
}

async function attemptUpload<T>(
  path: string,
  file: File | { metadata: UploadMetadata; bytes: Uint8Array },
  options: { timeoutMs?: number; signal?: AbortSignal },
): Promise<T> {
  const frame =
    file instanceof File
      ? encodeFrame(
          { filename: file.name, contentType: file.type },
          new Uint8Array(await file.arrayBuffer()),
        )
      : encodeFrame(file.metadata, file.bytes);

  const { pathname } = splitQuery(path, undefined);
  // Uploads are slow and large; the 10s default would cut off a real one.
  const signal = options.signal ?? AbortSignal.timeout(options.timeoutMs ?? 120_000);

  /**
   * Every route that accepts an upload is behind `requireRole("ADMIN")`, so
   * this header is not optional garnish — without it the API answers 401 and
   * the panel reports "Sign in to continue" at a moment when the operator is,
   * visibly, signed in.
   *
   * Note what is NOT here: a query string. `splitQuery` keeps only the path,
   * because anything an upload needs to say travels inside the sealed frame
   * instead — see `UploadMetadata`. A caller appending `?x=` would have it
   * silently dropped.
   */
  const token = getAccessToken();
  const auth: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  let url: string;
  let payload: BodyInit;
  let session: Session | null = null;

  if (encryptPayloads) {
    const sealed = await sealBinary(await getHandshake(), "POST", frame);
    session = sealed.session;
    url = absolute(pathname, { d: toQueryParam(sealed.header) });
    payload = sealed.body;
  } else {
    url = absolute(pathname);
    payload = frame as BodyInit;
  }

  const response = await fetch(url, {
    method: "POST",
    // Not multipart: a multipart body puts the filename in a plaintext header,
    // which is one of the things the frame exists to hide.
    headers: { "Content-Type": "application/octet-stream", ...auth },
    body: payload,
    // The refresh cookie, for the retry path above.
    credentials: "include",
    signal,
  });

  const parsed = await readBody(response, session);

  if (!response.ok) throw toApiError(url, response, parsed);

  return parsed as T;
}
